export function useFocusTrap(
  container: Ref<HTMLElement | null>,
  initialFocus: Ref<HTMLElement | null>
): void {
  const lastFocus = ref<HTMLElement | null>(null)
  const ignoreUtilFocusChanges = ref<boolean>(false)

  onMounted(() => {
    nextTick(() => {
      if (!container.value?.contains(initialFocus.value)) {
        focusFirstDescendant(container.value as HTMLElement)
      }

      initialFocus.value?.focus()
      lastFocus.value = initialFocus.value
    })
  })

  const focusTrap = (event: Event): void => {
    if (ignoreUtilFocusChanges.value) {
      return
    }

    if (container.value?.contains(event.target as Node)) {
      lastFocus.value = event.target as HTMLElement
    } else {
      focusFirstDescendant(container.value as HTMLElement)
      if (lastFocus.value === document.activeElement) {
        focusLastDescendant(container.value as HTMLElement)
      }
      lastFocus.value = document.activeElement as HTMLElement
    }
  }

  const focusLastDescendant = (element: HTMLElement): boolean => {
      for (let i = element.childNodes.length - 1; i >= 0; i--) {
        const child = element.childNodes[i]
        if (
          attemptFocus(child as HTMLElement) ||
          focusLastDescendant(child as HTMLElement)
        ) {
          return true
        }
      }
      return false
    }

  const focusFirstDescendant = (element: HTMLElement): boolean => {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i]
      if (
        attemptFocus(child as HTMLElement) ||
        focusFirstDescendant(child as HTMLElement)
      ) {
        return true
      }
    }
    return false
  }

  const attemptFocus = (element: HTMLElement): boolean => {
    if (!isFocusable(element)) {
      return false
    }

    ignoreUtilFocusChanges.value = true
    try {
      element.focus()
    } catch (e) {}

    ignoreUtilFocusChanges.value = false
    return document.activeElement === element
  }

  useEventListener(document, 'focus', focusTrap, true)
}

function isFocusable(element: HTMLElement | HTMLButtonElement | HTMLAnchorElement | HTMLInputElement | HTMLAnchorElement) {
  if (element.tabIndex < 0) {
    return false
  }

  if ((element as HTMLButtonElement).disabled) {
    return false
  }

  switch (element.nodeName) {
    case 'A':
      return !!(element as HTMLAnchorElement).href && (element as HTMLAnchorElement).rel !== 'ignore'
    case 'INPUT':
      return (element as HTMLInputElement).type !== 'hidden'
    case 'BUTTON':
    case 'SELECT':
    case 'TEXTAREA':
      return true
    default:
      return false
  }
}